import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Layout } from 'antd';

import {
	PointsSubscriptions,
	LeaderboardSubscriptions,
	EventSubscriptions,
	PageSubscriptions,
	EventAttendeesSubscriptions,
	AnnouncementSubscriptions,
	PostSubscriptions,
	EventMediaSubscriptions,
	MediaSubscriptions,
	LibrarySubscriptions,
	KudosBadgesSubscriptions,
	EventGroupsSubscriptions,
	GroupSubcriptions,
	AttendeeConnectionsSubscriptions,
	EventConnectionSubscriptions,
	CheckInsSubscriptions,
	EventCheckinSubscriptions,
	WidgetSubscriptions,
	PodcastSubscriptions,
	HashtagSubscriptions,
} from 'components/WebsocketSubscriptions';
import SideMenu from '../../components/SideMenu';

import EventDetailPlaceholder from 'components/EventDetailPlaceholder';
import { getEventNavigation } from './eventNavigation';
import agendaRoutes from './agendaRoutes';
import { isSaaSAdmin } from '@/redux/util';
import { lazyWithReload } from '@/utils';

import { WidgetContextProvider } from '@/WidgetContextProvider';

const Dashboard = lazyWithReload(() => import('./routes/Dashboard'));
const Settings = lazyWithReload(() => import('./routes/Settings'));
const Pages = lazyWithReload(() => import('./routes/Pages'));
const Media = lazyWithReload(() => import('./routes/Media'));
const MediaDetail = lazyWithReload(() => import('./routes/MediaDetail'));
const MediaLibraryDetail = lazyWithReload(() => import('./routes/MediaLibraryDetail'));
const PageDetail = lazyWithReload(() => import('./routes/PageDetail'));
const LeaderboardDetail = lazyWithReload(() => import('./routes/LeaderboardDetail'));
const Attendees = lazyWithReload(() => import('./routes/Attendees'));
const AttendeeDetail = lazyWithReload(() => import('../AttendeeDetail'));
const AttendeeMaterials = lazyWithReload(() => import('./routes/AttendeeMaterials'));
const MaterialLibraryDetail = lazyWithReload(() => import('./routes/MaterialLibraryDetail'));
const Groups = lazyWithReload(() => import('./routes/Groups'));
const GroupDuplicate = lazyWithReload(() => import('./routes/Groups/GroupDuplicate'));
const GroupDetail = lazyWithReload(() => import('./routes/Groups/GroupDetail'));
const Navigation = lazyWithReload(() => import('./routes/Navigation'));
const HomeScreen = lazyWithReload(() => import('./routes/HomeScreen'));
const Theme = lazyWithReload(() => import('./routes/Theme'));
const Announcements = lazyWithReload(() => import('./routes/Announcements'));
const SocialFeed = lazyWithReload(() => import('./routes/SocialFeed'));
const Audit = lazyWithReload(() => import('./routes/Audit'));
const Report = lazyWithReload(() => import('./routes/Report'));
const KudosAndBadges = lazyWithReload(() => import('./routes/KudosAndBadges'));
const Leaderboards = lazyWithReload(() => import('./routes/Leaderboards'));
const PointSettings = lazyWithReload(() => import('./routes/PointSettings'));
const PointSystem = lazyWithReload(() => import('./routes/PointSystem'));
const PointSystemReport = lazyWithReload(() => import('./routes/PointSystemReport'));
const Leaderboard = lazyWithReload(() => import('../../components/Kudos/Leaderboard'));
const AIPhotobooth = lazyWithReload(() => import('./routes/AIPhotobooth'));
const PhotoBooth = lazyWithReload(() => import('./routes/PhotoBooth'));
const PhotoBoothDetail = lazyWithReload(() => import('./routes/PhotoBooth/PhotoBoothDetail'));
const PhotoBoothMedia = lazyWithReload(() => import('./routes/PhotoBooth/PhotoBoothMedia'));
const Connections = lazyWithReload(() => import('./routes/Connections'));
const ConnectionSettings = lazyWithReload(() => import('./routes/ConnectionSettings'));
const CheckIns = lazyWithReload(() => import('./routes/CheckIns'));
const CheckinSettings = lazyWithReload(() => import('./routes/CheckinSettings'));
const WidgetBuilder = lazyWithReload(() => import('./routes/WidgetBuilder'));
const Podcasts = lazyWithReload(() => import('./routes/Podcasts'));
const PodcastDetail = lazyWithReload(() => import('./routes/PodcastDetail'));

const AIPhotoBoothCreateForm = lazyWithReload(() =>
	import('./routes/AIPhotobooth/AIPhotoboothDetail/AIPhotoBoothCreateForm')
);
const AIPhotoBoothUpdateForm = lazyWithReload(() =>
	import('./routes/AIPhotobooth/AIPhotoboothDetail/AIPhotoBoothUpdateForm')
);
const AIPhotoBoothMedia = lazyWithReload(() => import('./routes/AIPhotobooth/AIPhotoBoothMedia'));
const LQSDetail = lazyWithReload(() => import('./routes/LQSDetail'));
const LQSList = lazyWithReload(() => import('./routes/LQSList'));
const LQSSettings = lazyWithReload(() => import('./routes/LQSSettings'));
const Broadcasts = lazyWithReload(() => import('./routes/Broadcasts'));
const QuizList = lazyWithReload(() => import('./routes/QuizList'));
const QuizDetail = lazyWithReload(() => import('./routes/QuizDetail'));
const QuizSettings = lazyWithReload(() => import('./routes/QuizSettings'));
const QuizLeaderboard = lazyWithReload(() => import('./routes/QuizLeaderboard'));
const PollList = lazyWithReload(() => import('./routes/PollList'));
const PollDetail = lazyWithReload(() => import('./routes/PollDetail'));
const PollSettings = lazyWithReload(() => import('./routes/PollSettings'));
const NotFound = lazyWithReload(() => import('./routes/NotFound'));

const { Content } = Layout;

export default props => {
	// without hasSider, antd only lays the Sider out beside the content after it mounts, so the
	// first render is full width and charts that mount then (like Syncs) size themselves too wide
	return (
		<Layout hasSider>
			<SideMenu
				event={props.event}
				client={props.client}
				menuData={getEventNavigation(props.event, props.roles, isSaaSAdmin())}
				adminSSOProfile={props.adminSSOProfile}
			/>
			<Content>
				<Suspense fallback={<EventDetailPlaceholder />}>
					<Routes>
						<Route
							path={'dashboard'}
							element={
								<EventSubscriptions>
									<Dashboard {...props} />
								</EventSubscriptions>
							}
						/>
						<Route
							path={'settings'}
							element={
								<EventSubscriptions>
									<Settings {...props} />
								</EventSubscriptions>
							}
						/>
						{agendaRoutes(props)}
						<Route
							path={'pages'}
							element={
								<PageSubscriptions>
									<Pages {...props} />
								</PageSubscriptions>
							}
						/>
						<Route
							path={'pages/:page_id'}
							element={
								<WidgetContextProvider>
									<PageSubscriptions>
										<WidgetSubscriptions>
											<PageDetail {...props} />
										</WidgetSubscriptions>
									</PageSubscriptions>
								</WidgetContextProvider>
							}
						/>
						<Route
							path={'media'}
							element={
								<EventMediaSubscriptions>
									<Media {...props} />
								</EventMediaSubscriptions>
							}
						/>
						<Route
							path={'media/:id'}
							element={
								<MediaSubscriptions>
									<MediaDetail {...props} />
								</MediaSubscriptions>
							}
						/>
						<Route
							path={'library/:id'}
							element={
								<LibrarySubscriptions>
									<MediaLibraryDetail {...props} />
								</LibrarySubscriptions>
							}
						/>
						<Route
							path={'attendees'}
							element={
								<EventAttendeesSubscriptions>
									<Attendees {...props} />
								</EventAttendeesSubscriptions>
							}
						/>
						<Route path={'attendees/:id'} element={<AttendeeDetail {...props} />} />
						<Route
							path={'groups'}
							element={
								<EventGroupsSubscriptions>
									<Groups {...props} />
								</EventGroupsSubscriptions>
							}
						/>
						<Route
							path={'groups/duplicate/:id'}
							element={
								<GroupSubcriptions route="duplicate">
									<GroupDuplicate {...props} />
								</GroupSubcriptions>
							}
						/>
						<Route
							path={'groups/:id'}
							element={
								<GroupSubcriptions>
									<GroupDetail {...props} />
								</GroupSubcriptions>
							}
						/>
						<Route
							path={'home'}
							element={
								<WidgetContextProvider>
									<EventSubscriptions>
										<WidgetSubscriptions>
											<HomeScreen {...props} />
										</WidgetSubscriptions>
									</EventSubscriptions>
								</WidgetContextProvider>
							}
						/>
						<Route
							path={'widgetHome'}
							element={
								<WidgetContextProvider>
									<EventSubscriptions>
										<WidgetSubscriptions>
											<WidgetBuilder {...props} />
										</WidgetSubscriptions>
									</EventSubscriptions>
								</WidgetContextProvider>
							}
						/>
						<Route
							path={'theme'}
							element={
								<EventSubscriptions>
									<Theme {...props} />
								</EventSubscriptions>
							}
						/>
						<Route
							path={'navigation'}
							element={
								<EventSubscriptions>
									<Navigation {...props} />
								</EventSubscriptions>
							}
						/>
						<Route
							path={'socialFeed'}
							element={
								<EventSubscriptions>
									<PostSubscriptions>
										<HashtagSubscriptions>
											<SocialFeed {...props} />
										</HashtagSubscriptions>
									</PostSubscriptions>
								</EventSubscriptions>
							}
						/>
						<Route
							path={'announcements'}
							element={
								<AnnouncementSubscriptions>
									<Announcements {...props} />
								</AnnouncementSubscriptions>
							}
						/>
						<Route path={'audit'} element={<Audit {...props} />} />
						<Route path={'report'} element={<Report {...props} />} />
						<Route
							path={'kudos/leaderboard'}
							element={
								<KudosBadgesSubscriptions kudosOrBadges="kudosLeaderboard">
									<Leaderboard {...props} />
								</KudosBadgesSubscriptions>
							}
						/>
						<Route path={'kudos'} element={<KudosAndBadges {...props} kudosOrBadges={'kudos'} />} />
						<Route
							path={'badges'}
							element={<KudosAndBadges {...props} kudosOrBadges={'badges'} />}
						/>
						<Route
							path={'leaderboards'}
							element={
								<LeaderboardSubscriptions>
									<Leaderboards {...props} />
								</LeaderboardSubscriptions>
							}
						/>
						<Route
							path={'leaderboards/:id'}
							element={
								<LeaderboardSubscriptions>
									<LeaderboardDetail {...props} />
								</LeaderboardSubscriptions>
							}
						/>
						<Route
							path={'points'}
							element={
								<PointsSubscriptions>
									<PointSystem {...props} />
								</PointsSubscriptions>
							}
						/>
						<Route
							path={'points/settings'}
							element={
								<EventSubscriptions>
									<PointsSubscriptions>
										<PointSettings {...props} />
									</PointsSubscriptions>
								</EventSubscriptions>
							}
						/>
						<Route path={'points/attendee/:id'} element={<PointSystemReport {...props} />} />
						<Route path={'ai-photobooth'} element={<AIPhotobooth {...props} />} />
						<Route path={'ai-photobooth/create'} element={<AIPhotoBoothCreateForm {...props} />} />
						<Route path={'ai-photobooth/:pbId'} element={<AIPhotoBoothUpdateForm {...props} />} />
						<Route path={'ai-photobooth/:pbId/media'} element={<AIPhotoBoothMedia {...props} />} />
						<Route path={'photobooth/:id/media'} element={<PhotoBoothMedia {...props} />} />
						<Route path={'photobooth/create'} element={<PhotoBoothDetail {...props} />} />
						<Route path={'photobooth/:id'} element={<PhotoBoothDetail {...props} />} />
						<Route path={'photobooth'} element={<PhotoBooth {...props} />} />
						<Route path={'broadcasts/:id'} element={<Broadcasts {...props} />} />
						<Route path={'broadcasts'} element={<Broadcasts {...props} />} />
						<Route path={'lqs/:id'} element={<LQSDetail {...props} />} />
						<Route path={'lqs'} element={<LQSList {...props} />} />
						<Route path={'lqs/:id/settings'} element={<LQSSettings {...props} />} />
						<Route path={'quizzes/leaderboard'} element={<QuizLeaderboard {...props} />} />
						<Route path={'quizzes'} element={<QuizList {...props} />} />
						<Route path={'quizzes/:quizID'} element={<QuizDetail {...props} />} />
						<Route path={'quizzes/:quizID/settings'} element={<QuizSettings {...props} />} />
						<Route path={'polls'} element={<PollList {...props} />} />
						<Route path={'polls/:pollID'} element={<PollDetail {...props} />} />
						<Route path={'polls/:pollID/settings'} element={<PollSettings {...props} />} />
						<Route
							path={'quizzes/not-found'}
							element={<NotFound message={'The quiz was not found'} />}
						/>
						<Route
							path={'polls/not-found'}
							element={<NotFound message={'The poll was not found'} />}
						/>
						<Route
							path={'connections/settings'}
							element={
								<EventConnectionSubscriptions>
									<ConnectionSettings {...props} />
								</EventConnectionSubscriptions>
							}
						/>
						<Route
							path={'connections'}
							element={
								<AttendeeConnectionsSubscriptions>
									<Connections {...props} />
								</AttendeeConnectionsSubscriptions>
							}
						/>
						<Route
							path={'checkins/settings'}
							element={
								<EventCheckinSubscriptions>
									<CheckinSettings {...props} />
								</EventCheckinSubscriptions>
							}
						/>
						<Route
							path={'checkins'}
							element={
								<CheckInsSubscriptions>
									<CheckIns {...props} />
								</CheckInsSubscriptions>
							}
						/>
						<Route path={'materials'} element={<AttendeeMaterials {...props} />} />
						<Route
							path={'materials/library/:id'}
							element={
								<LibrarySubscriptions>
									<MaterialLibraryDetail {...props} />
								</LibrarySubscriptions>
							}
						/>
						<Route
							path={'podcasts'}
							element={
								<PodcastSubscriptions>
									<Podcasts {...props} />
								</PodcastSubscriptions>
							}
						/>
						<Route
							path={'podcasts/:id'}
							element={
								<PodcastSubscriptions>
									<PodcastDetail {...props} />
								</PodcastSubscriptions>
							}
						/>
						<Route path={'podcasts/create'} element={<PodcastDetail {...props} />} />
					</Routes>
				</Suspense>
			</Content>
		</Layout>
	);
};
